/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";

import moment from "moment";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";

import { colors, ExpandMoreIcon } from "@dotnet/telematics-design-system";

import { useDateTime } from "../../../../../../../shared";
import Badge from "../../../../../../Badge/Badge";
import { useStyles } from "../styles/styles";

interface CompanyCardAuthAccordionProps {
  expandedPanel: string;
  entry: any;
  index: number;
  isLast: boolean;
  rootPadding: string;
  onCollapse: (value: string, expanded: boolean) => void;
  actionButton?: React.JSX.Element;
}

const CompanyCardAuthAccordion = ({
  index,
  entry,
  expandedPanel,
  isLast,
  rootPadding,
  onCollapse,
  actionButton
}: CompanyCardAuthAccordionProps) => {
  const { textDark, textSecondary } = colors;

  const { t } = useTranslation();
  const { currentDateFormat } = useDateTime();

  const isExpanded = expandedPanel === `panel${index}`;

  const {
    mainAccordionCSS,
    headerMainCSS,
    versionRowCSS,
    versionBadgeCSS,
    actionButtonCSS,
    releaseNotesCSS
  } = useStyles(isExpanded, isLast, rootPadding);

  const { releaseDate, version, fileSize, releaseNotes } = entry;

  const formattedDate = moment(releaseDate, "MM/DD/YYYY").format(
    currentDateFormat
  ); // temporary solution until BE returns ISO formatted date

  const titleSubtext = `${formattedDate} - ${fileSize}`;

  return (
    <Accordion
      elevation={0}
      data-testid="news-accordion"
      expanded={isExpanded}
      onChange={(_, expanded) => onCollapse(`panel${index}`, expanded)}
      disableGutters
      css={css(mainAccordionCSS)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        id="accordionHeader"
        key={index}
      >
        <div css={css(headerMainCSS)}>
          <div css={css(versionRowCSS)}>
            <Typography variant="body1Medium">{version}</Typography>

            <div css={css(versionBadgeCSS)}>
              {index === 0 && (
                <Badge color="success">{t("General##latest version")}</Badge>
              )}
            </div>
          </div>

          {releaseDate && (
            <Typography variant="body2Medium" sx={{ color: textSecondary }}>
              {titleSubtext}
            </Typography>
          )}
        </div>
        {actionButton && <div css={css(actionButtonCSS)}>{actionButton}</div>}
      </AccordionSummary>

      <AccordionDetails css={css(releaseNotesCSS)}>
        <Typography
          component="span"
          variant="body2Medium"
          color={textSecondary}
        >
          {t("Dialog##release notes")}:
        </Typography>

        <ul>
          {releaseNotes?.map((note: string) => (
            <li key={note}>
              <Typography
                component="span"
                variant="body2Medium"
                color={textDark}
              >
                {note}
              </Typography>
            </li>
          ))}
        </ul>
      </AccordionDetails>
    </Accordion>
  );
};

export default CompanyCardAuthAccordion;
